import type { HelpArticle } from "./help-content";

export interface HelpSearchHit {
  article: HelpArticle;
  rank: number;
  /** Set when the hit came from a keyword the customer cannot see on the card. */
  matchedKeyword: string | null;
}

/**
 * Two normalisations, doing different jobs.
 *
 * `squash` strips everything that is not alphanumeric, so "trading view",
 * "Trading-View" and "tradingview" collapse to one string. That handles the
 * whole class of spacing, case and punctuation variants without anyone
 * authoring a synonym for them.
 *
 * `loose` keeps word boundaries so a multi-word query can be matched token by
 * token — "webhook config" should find "Webhook configuration".
 */
const squash = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");
const loose = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

function matches(text: string, querySquash: string, tokens: string[]): boolean {
  if (squash(text).includes(querySquash)) return true;
  const haystack = loose(text);
  return tokens.length > 0 && tokens.every((token) => haystack.includes(token));
}

const RANK_TITLE = 3;
const RANK_KEYWORD = 2;
const RANK_BODY = 1;

export function searchHelp(
  articles: HelpArticle[],
  query: string
): HelpSearchHit[] {
  const querySquash = squash(query);
  if (!querySquash) return [];

  const tokens = loose(query).split(" ").filter(Boolean);
  const hits: HelpSearchHit[] = [];

  for (const article of articles) {
    const keywords = article.keywords.join(" ");
    const rest = `${article.summary} ${article.category} ${article.slug}`;

    let rank = 0;
    if (matches(article.title, querySquash, tokens)) rank = RANK_TITLE;
    else if (matches(keywords, querySquash, tokens)) rank = RANK_KEYWORD;
    else if (matches(rest, querySquash, tokens)) rank = RANK_BODY;

    if (rank === 0) continue;

    // If nothing on the card explains the hit, name the keyword that caused it.
    // Otherwise the result looks arbitrary and search stops feeling trustworthy.
    const visible = `${article.title} ${article.summary}`;
    const matchedKeyword = matches(visible, querySquash, tokens)
      ? null
      : (article.keywords.find((keyword) =>
          matches(keyword, querySquash, tokens)
        ) ?? null);

    hits.push({ article, rank, matchedKeyword });
  }

  return hits.sort(
    (a, b) => b.rank - a.rank || a.article.title.localeCompare(b.article.title)
  );
}
